import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { renderAccordionBorder, renderArrow } from 'utils';
import styles from './Accordion.module.scss';
import { ReactComponent as ArrowDownIcon } from 'assets/images/ArrowDown.svg';
import { ReactComponent as ShadowArrowDownIcon } from 'assets/images/ShadowArrowDown.svg';

const Accordion = ({
  classNames = { content: '' },
  title = 'Accordion',
  toggleDisabled = false,
  noBreak = false,
  color = '#7d411c',
  type = '',
  isOpened: initOpened = false,
  children
}) => {
  const accordionRef = useRef();
  const [isOpened, setIsOpened] = useState(initOpened);
  const [showMoreIcon, setShowMoreIcon] = useState(false);

  const isExpanded = isOpened || toggleDisabled;

  // No dependency array: re-measures after every render so content changes are
  // caught. Resize and font loading also reflow the content, so re-check on
  // both.
  useEffect(() => {
    let cancelled = false;
    const measure = () => {
      if (cancelled) return;
      const el = accordionRef.current;
      if (!el || !isExpanded) {
        setShowMoreIcon(false);
        return;
      }
      // The element that scrolls may be the content div itself or a scrollable
      // wrapper the children bring along (EventList clamps event details to
      // its own inner scroller), so check every scroll container in the
      // subtree. The +1 absorbs subpixel rounding in scrollHeight/clientHeight.
      const overflowing = [el, ...el.querySelectorAll('*')].some((node) => {
        const { overflowY } = window.getComputedStyle(node);
        return (
          (overflowY === 'auto' || overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight + 1
        );
      });
      setShowMoreIcon(overflowing);
    };

    measure();
    window.addEventListener('resize', measure);
    document.fonts?.ready.then(measure);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', measure);
    };
  });

  const handleClickHeader = () => {
    if (toggleDisabled) return;

    setIsOpened(!isOpened);
  };

  return (
    <div className={clsx(styles.accordion, noBreak && styles['accordion--no-break'])}>
      <div
        className={clsx(styles.accordion__header__wrapper, type === 'event' && styles.accordion__header__event__wrapper)}
        style={{ color: color }}
      >
        <div
          className={clsx(styles.accordion__header, type === 'event' && styles.accordion__event__header, !toggleDisabled && styles['accordion__header--clickable'])}
          onClick={handleClickHeader}
        >
          <span dangerouslySetInnerHTML={{ __html: title }} />
          {!toggleDisabled ? (
            <div>
              {renderArrow(color, clsx(styles.triangle, isOpened && styles['triangle--up']))}
            </div>
          ) : null}
        </div>
      </div>

      {(color && type === 'event') && renderAccordionBorder(color, styles)}

      <div
        className={clsx(
          classNames.content,
          styles.accordion__content,
          type === 'event' && isOpened && styles.accordion__event__content,
          isExpanded && styles['accordion__content--opened']
        )}
        ref={accordionRef}
      >
        {children}

        {showMoreIcon && type === 'event' && (
          <div className={styles.accordion__event__content__arrowdown}>
            <ArrowDownIcon />
          </div>
        )}
      </div>

      {showMoreIcon && type !== 'event' && (
        <div className={styles.accordion__content__arrowdown}>
          <ShadowArrowDownIcon />
        </div>
      )}

      {(color && type === 'event' && isOpened) && renderAccordionBorder(color, styles)}
    </div>
  );
};

export default Accordion;